<template>
  <BaseContent>
    <div class="flex justify-between mb-4">
      <div class="text-gray-900 text-xl font-medium">{{ fio }}</div>

      <Button
        v-if="hasAgent"
        icon="pi pi-user-edit"
        :disabled="updatePending || loadPending"
        @click="toggleEdit"
      ></Button>
    </div>

    <div class="bg-gray-100 p-4 rounded-2xl">
      <div class="col-span-full ps-0 p-2 flex">
        <div class="w-1/3 text-gray-500 font-medium">Email</div>
        <template v-if="isEditMode && hasAgent">
          <div class="w-full">
            <InputText
              :modelValue="displayedProfile.email || ''"
              class="w-full"
              disabled
            />
          </div>
        </template>
        <template v-else>
          <div class="text-gray-900">{{ displayedProfile.email || 'Не заполнено' }}</div>
        </template>
      </div>

      <div class="col-span-full ps-0 p-2 flex items-center">
        <div class="w-1/3 text-gray-500 font-medium">
          Телефон
          <span
            v-if="isEditMode && hasAgent"
            class="text-red-500"
            >*</span
          >
        </div>
        <template v-if="isEditMode && hasAgent">
          <div class="flex flex-col gap-1 w-full">
            <InputPhone
              v-model="phone"
              class="w-full"
              :disabled="updatePending"
              :invalid="Boolean(errors.phone)"
              @blur="setPhoneTouched(true)"
              @update:modelValue="onPhoneInput"
            />
            <BaseErrorMessage v-if="errors.phone">{{ errors.phone }}</BaseErrorMessage>
          </div>
        </template>
        <template v-else>
          <div class="text-gray-900">
            {{ displayedProfile.phone ? `+7${displayedProfile.phone}` : 'Не заполнено' }}
          </div>
        </template>
      </div>
      <div
        v-if="isInd"
        class="col-span-full ps-0 p-2 flex items-center"
      >
        <div class="w-1/3 text-gray-500 font-medium">
          ИНН
          <span
            v-if="isEditMode"
            class="text-red-500"
            >*</span
          >
        </div>
        <template v-if="isEditMode">
          <div class="flex flex-col gap-1 w-full">
            <InputText
              v-model="inn"
              class="w-full"
              :disabled="updatePending || !!agentData?.inn"
              :invalid="Boolean(errors.inn)"
              :maxlength="12"
              @blur="setInnTouched(true)"
            />
            <BaseErrorMessage v-if="errors.inn">{{ errors.inn }}</BaseErrorMessage>
          </div>
        </template>
        <template v-else>
          {{ displayedProfile.inn || 'Не заполнено' }}
        </template>
      </div>

      <template v-if="!isEditMode && hasAgent">
        <div
          v-if="!indLegal"
          class="col-span-full ps-0 p-2 flex"
        >
          <div class="w-1/3 text-gray-500 font-medium">Адрес юридический</div>
          <div class="text-gray-900">{{ displayedProfile.address_text || 'Не заполнено' }}</div>
        </div>

        <div class="col-span-full ps-0 p-2 flex">
          <div class="w-1/3 text-gray-500 font-medium">Город ведения деятельности</div>
          <div class="text-gray-900">{{ displayedProfile.address_of_operation || 'Не заполнено' }}</div>
        </div>

        <div class="col-span-full ps-0 p-2 flex">
          <div class="w-1/3 text-gray-500 font-medium">БИК банка</div>
          <div class="text-gray-900">{{ displayedRequisites.bik || 'Не заполнено' }}</div>
        </div>

        <div class="col-span-full ps-0 p-2 flex">
          <div class="w-1/3 text-gray-500 font-medium">Банк</div>
          <div class="text-gray-900">{{ displayedRequisites.payment_bank || 'Не заполнено' }}</div>
        </div>

        <div class="col-span-full ps-0 p-2 flex">
          <div class="w-1/3 text-gray-500 font-medium">Корр. счет</div>
          <div class="text-gray-900">{{ displayedRequisites.correspondent_account || 'Не заполнено' }}</div>
        </div>

        <div class="col-span-full ps-0 p-2 flex">
          <div class="w-1/3 text-gray-500 font-medium">Расчетный счет</div>
          <div class="text-gray-900">{{ displayedRequisites.payment_account || 'Не заполнено' }}</div>
        </div>
      </template>

      <template v-else-if="hasAgent">
        <div
          v-if="!indLegal"
          class="col-span-full ps-0 p-2 flex items-center"
        >
          <div class="w-1/3 text-gray-500 font-medium">
            Адрес юридический <span class="text-red-500">*</span>
          </div>
          <div class="w-full flex flex-col gap-1">
            <InputAddress
              v-model="addressText"
              class="w-full"
              :disabled="updatePending"
              :invalid="Boolean(errors.address_text)"
              placeholder="Начните вводить адрес"
              @blur="setAddressTextTouched(true)"
            />
            <BaseErrorMessage v-if="errors.address_text">{{ errors.address_text }}</BaseErrorMessage>
          </div>
        </div>

        <div class="col-span-full ps-0 p-2 flex items-center">
          <div class="w-1/3 text-gray-500 font-medium">
            Город ведения деятельности
            <span class="text-red-500">*</span>
          </div>
          <div class="w-full flex flex-col gap-1">
            <DadataInputAddressInput
              id="address_of_operation"
              v-model="address_of_operation"
              class="h-10 leading-4"
              placeholder="Начните вводить город"
              :disabled="updatePending"
              :err-msg="errors.address_of_operation"
              @blur="setAddressOfOperationTouched(true)"
            />
          </div>
        </div>

        <div class="col-span-full ps-0 p-2 flex items-center">
          <div class="w-1/3 text-gray-500 font-medium">
            БИК банка
            <span class="text-red-500">*</span>
          </div>
          <div class="w-full flex flex-col gap-1">
            <InputBank
              v-model="bik"
              class="w-full"
              placeholder="Начните вводить БИК"
              :disabled="updatePending"
              :invalid="Boolean(errors.bik)"
              @itemSelect="onBankSelect"
              @blur="setBikTouched(true)"
              @update:modelValue="onBikInput"
            />
            <BaseErrorMessage v-if="errors.bik">{{ errors.bik }}</BaseErrorMessage>
          </div>
        </div>
        <div class="col-span-full ps-0 p-2 flex items-center">
          <div class="w-1/3 text-gray-500 font-medium">
            Банк
            <span class="text-red-500">*</span>
          </div>
          <div class="w-full flex flex-col gap-1">
            <InputText
              v-model.trim="paymentBank"
              class="w-full"
              :disabled="updatePending"
              :invalid="Boolean(errors.payment_bank)"
              @update:modelValue="onPaymentBankInput"
            />
            <BaseErrorMessage v-if="errors.payment_bank">{{ errors.payment_bank }}</BaseErrorMessage>
          </div>
        </div>

        <div class="col-span-full ps-0 p-2 flex items-center">
          <div class="w-1/3 text-gray-500 font-medium">
            Корр. счет
            <span class="text-red-500">*</span>
          </div>
          <div class="w-full flex flex-col gap-1">
            <InputText
              v-model="correspondentAccount"
              class="w-full"
              :disabled="updatePending"
              :invalid="Boolean(errors.correspondent_account)"
              maxlength="20"
              inputmode="numeric"
              @keydown="preventNonDigits"
              @blur="setCorrespondentAccountTouched(true)"
              @update:modelValue="onCorrespondentAccountInput"
            />
            <BaseErrorMessage v-if="errors.correspondent_account">
              {{ errors.correspondent_account }}
            </BaseErrorMessage>
          </div>
        </div>

        <div class="col-span-full ps-0 p-2 flex items-center">
          <div class="w-1/3 text-gray-500 font-medium">
            Расчетный счет
            <span class="text-red-500">*</span>
          </div>
          <div class="w-full flex flex-col gap-1">
            <InputText
              v-model="paymentAccount"
              class="w-full"
              :disabled="updatePending"
              :invalid="Boolean(errors.payment_account)"
              maxlength="20"
              inputmode="numeric"
              @keydown="preventNonDigits"
              @blur="setPaymentAccountTouched(true)"
              @update:modelValue="onPaymentAccountInput"
            />
            <BaseErrorMessage v-if="errors.payment_account">{{ errors.payment_account }}</BaseErrorMessage>
          </div>
        </div>

        <div class="col-span-full ps-0 p-2 flex justify-end gap-2">
          <Button
            outlined
            label="Отмена"
            :disabled="updatePending"
            @click="cancelEdit"
          />
          <Button
            label="Сохранить"
            :loading="updatePending"
            :disabled="!meta.valid"
            @click="save"
          />
        </div>
      </template>
    </div>
  </BaseContent>
</template>

<script setup lang="ts">
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import {
  Agent,
  AgentUpdate,
  BaseContent,
  DadataBankSuggestion,
  InputAddress,
  InputBank,
  InputPhone,
  User,
  useGetUser,
  useGetAgentById,
  useGetAgentList,
  useUpdateUser,
  useUpdateAgent,
  useUpdateSalespointSelfRegister,
  addressToSalespointFields
} from '@/shared';
import { useGlobalToast as useToast } from '@/shared/lib/toast';
import { computed, onMounted, ref } from 'vue';
import { useAuthModel } from '@/features/auth';
import { useUserModel } from '@/entities/user/model';
import { useField, useForm } from 'vee-validate';
import { useSalespointStore } from '@/store';
import * as yup from 'yup';
import type { Address } from '@/shared';
import {
  getAddressOfOperationForSubmission,
  getAddressOfOperationForUsage,
  isEmptyAddress,
  type BackendAddressOfOperation
} from '@/shared/lib/dadataAddressHelpers';
import type { IDadataAddress } from '@/shared/model/dadata';
import BaseErrorMessage from '@/shared/components-2/base/BaseErrorMessage.vue';
import DadataInputAddressInput from '@/shared/components/DadataInputAddressInput.vue';
import { useLegalForm } from '@/entities/salespoint/model/useLegalForm';

const props = defineProps<{
  user: User;
}>();

const authModel = useAuthModel();
const userModel = useUserModel();
const { isInd } = useLegalForm();

const salespointStore = useSalespointStore();
const { add: addToast } = useToast();
const { post: getAgentListPost, pending: getAgentListPending } = useGetAgentList();
const { post: getAgentByIdPost, pending: getAgentByIdPending } = useGetAgentById();
const { post: updateAgentPost, pending: updateAgentPending } = useUpdateAgent();
const { post: updateSalespointPost, pending: updateSalespointPending } = useUpdateSalespointSelfRegister();
const { post: getUserPost } = useGetUser();
const { post: updateUserPost, pending: updateUserPending } = useUpdateUser();

const updatePending = computed(
  () => updateAgentPending.value || updateSalespointPending.value || updateUserPending.value
);

const isEditMode = ref<boolean>(false);
const agentId = ref<number | null>(null);
const agentData = ref<Agent | null>(null);
const profileUser = ref<User | null>(null);
const isBankSelectedFromList = ref<boolean>(false);

const indLegal = computed(() => agentData?.value?.legal_form === 'IND');
const isLegalAddressRequired = computed(() => !indLegal.value);

function getAddressOfOperationDisplayText(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'string') return value;

  if (typeof value === 'object') {
    const dadata = getAddressOfOperationForUsage(value as BackendAddressOfOperation);
    if (dadata?.unrestricted_value) return dadata.unrestricted_value;

    const addressText = (value as { address_text?: unknown }).address_text;
    if (typeof addressText === 'string') return addressText;

    const cityValue = (value as { city?: unknown }).city;
    if (typeof cityValue === 'string') return cityValue;
    if (cityValue && typeof cityValue === 'object' && 'name' in cityValue) {
      const cityName = (cityValue as { name?: unknown }).name;
      return typeof cityName === 'string' ? cityName : '';
    }
  }

  return '';
}

function getSalespointAddressText(): string {
  return salespointStore.salespoint?.address_text?.trim() || '';
}

const validationSchema = yup.object({
  phone: yup
    .string()
    .required('Поле обязательное')
    .transform(value => (value ? value.replace(/\D/g, '') : value))
    .test('len', 'Телефон должен содержать 10 цифр', value => !value || value.length === 10),
  inn: yup
    .string()
    .test('required', 'Поле обязательное', value => {
      if (!isInd.value) return true;

      return !!value;
    })
    .length(12, 'ИНН должен содержать 12 цифр'),
  address_of_operation: yup
    .mixed<IDadataAddress>()
    .nullable()
    .test('required', 'Поле обязательное', value => !isEmptyAddress(value)),
  address_text: yup
    .mixed<string | Address>()

    .test('address-text', 'Укажите адрес', value => {
      if (!isLegalAddressRequired.value) return true;
      if (!value) return true;
      if (typeof value === 'string') return Boolean(value.trim());

      return Boolean(value.address_text?.trim());
    }),
  bik: yup
    .string()
    .required('БИК банка обязателен')
    .matches(/^\d+$/, 'БИК банка должен содержать только цифры')
    .length(9, 'БИК банка должен содержать ровно 9 цифр'),
  payment_bank: yup
    .string()
    .required('Банк обязателен')
    .test('from-backend', 'Выберите банк из выпадающего списка', () => isBankSelectedFromList.value),
  correspondent_account: yup
    .string()
    .required('Корр. счет обязателен')
    .matches(/^\d+$/, 'Корр. счет должен содержать только цифры')
    .length(20, 'Корр. счет должен содержать ровно 20 цифр'),
  payment_account: yup
    .string()
    .required('Расчетный счет обязателен')
    .matches(/^\d+$/, 'Расчетный счет должен содержать только цифры')
    .length(20, 'Расчетный счет должен содержать ровно 20 цифр')
});

type UserInfoFormValues = {
  phone: string;
  inn?: string | null;
  address_of_operation: IDadataAddress | null;
  address_text: string | Address;
  bik: string;
  payment_bank: string;
  correspondent_account: string;
  payment_account: string;
};

const { errors, validate, resetForm, meta } = useForm<UserInfoFormValues>({
  initialValues: {
    inn: '',
    phone: '',
    address_of_operation: null,
    address_text: '',
    bik: '',
    payment_bank: '',
    correspondent_account: '',
    payment_account: ''
  },
  validationSchema
});

function getFormValues(): UserInfoFormValues {
  return {
    phone: normalizeDigits(agentData.value?.phone || null, 10),
    inn: agentData.value?.inn,
    address_of_operation: getAddressOfOperationForUsage(
      profileUser.value?.address_of_operation as BackendAddressOfOperation | undefined
    ),
    address_text: getSalespointAddressText(),
    bik: displayedRequisites.value.bik || '',
    payment_bank: displayedRequisites.value.payment_bank || '',
    correspondent_account: displayedRequisites.value.correspondent_account || '',
    payment_account: displayedRequisites.value.payment_account || ''
  };
}

const { value: phone, setTouched: setPhoneTouched } = useField<string>('phone');
const { value: inn, setTouched: setInnTouched } = useField<string>('inn');

const { value: address_of_operation, setTouched: setAddressOfOperationTouched } =
  useField<IDadataAddress | null>('address_of_operation');
const { value: addressText, setTouched: setAddressTextTouched } = useField<string | Address>('address_text');
const { value: bik, setTouched: setBikTouched } = useField<string>('bik');
const { value: paymentBank } = useField<string>('payment_bank');
const { value: correspondentAccount, setTouched: setCorrespondentAccountTouched } =
  useField<string>('correspondent_account');
const { value: paymentAccount, setTouched: setPaymentAccountTouched } = useField<string>('payment_account');

const displayedProfile = computed(() => ({
  email: agentData.value?.email || props.user.email || null,
  phone: agentData.value?.phone || String(props.user.phone || '') || null,
  inn: agentData.value?.inn || null,
  address_of_operation: getAddressOfOperationDisplayText(profileUser.value?.address_of_operation) || null,
  address_text: getSalespointAddressText() || null
}));

const hasAgent = computed(() => userModel.hasAgentAccount && Boolean(agentData.value && agentId.value));
const loadPending = computed(() => getAgentListPending.value || getAgentByIdPending.value);
const displayedRequisites = computed(() => ({
  payment_bank: agentData.value?.payment_bank,
  payment_account: agentData.value?.payment_account,
  bik: agentData.value?.bank_bik,
  correspondent_account: agentData.value?.bank_ca
}));

const fio = computed(
  () => `${props.user.last_name} ${props.user.first_name} ${props.user.middle_name || ''}`
);

function toggleEdit() {
  isEditMode.value = !isEditMode.value;
  if (isEditMode.value) {
    resetForm({ values: getFormValues() });
    isBankSelectedFromList.value = Boolean(
      displayedRequisites.value.payment_bank && displayedRequisites.value.bik
    );
  }
}

function cancelEdit() {
  isEditMode.value = false;
  resetForm({ values: getFormValues() });
  isBankSelectedFromList.value = Boolean(
    displayedRequisites.value.payment_bank && displayedRequisites.value.bik
  );
}

function buildAgentUpdatePayload(agentName: string): AgentUpdate {
  return {
    inn: inn.value,
    name: agentName || null,
    email: displayedProfile.value.email || null,
    phone: phone.value || null,
    bank_bik: bik.value || null,
    bank_ca: correspondentAccount.value || null,
    payment_bank: paymentBank.value || null,
    payment_account: paymentAccount.value || null
  };
}

function buildSalespointUpdatePayload(agentName: string) {
  return {
    name: agentName || null,
    email: displayedProfile.value.email || null,
    phone: phone.value || null,
    bik: bik.value || null,
    correspondent_account: correspondentAccount.value || null,
    payment_bank: paymentBank.value || null,
    payment_account: paymentAccount.value || null
  };
}

async function save() {
  const valid = await validate();
  if (!valid.valid) return;

  const agentName = props.user.title || fio.value.trim();
  const addressOfOperationPayload = getAddressOfOperationForSubmission(
    address_of_operation.value as IDadataAddress
  ).address_of_operation;
  const agentUpdatePayload = buildAgentUpdatePayload(agentName);
  const salespointUpdatePayload = buildSalespointUpdatePayload(agentName);

  const isIndividual = agentData.value?.legal_form === 'IND';

  const salespointAddressFields = isIndividual
    ? {
        address: null,
        address_text: null,
        address_country: null,
        address_postcode: null,
        address_region: null,
        address_city: null,
        address_street: null,
        address_house: null,
        address_flat: null
      }
    : addressToSalespointFields(addressText.value, salespointStore.salespoint);

  const userRes = await updateUserPost({
    _data: {
      id: props.user.id,
      email: displayedProfile.value.email || props.user.email,
      phone: phone.value,
      address_of_operation: addressOfOperationPayload
    }
  });

  if (userRes?._code !== 0) {
    addToast({
      severity: 'error',
      summary: 'Профиль',
      detail: userRes?._message || 'Не удалось сохранить данные пользователя'
    });
    return;
  }

  const salespointId = salespointStore.salespoint?.id;
  if (salespointId && !indLegal.value) {
    const salespointRes = await updateSalespointPost({
      _data: {
        ...salespointUpdatePayload,
        id: salespointId,
        ...salespointAddressFields
      }
    });

    if (salespointRes?._code !== 0) {
      addToast({
        severity: 'error',
        summary: 'Профиль',
        detail: salespointRes?._message || 'Не удалось сохранить данные торговой точки'
      });
      return;
    }

    if (salespointStore.salespoint) {
      salespointStore.set({
        ...salespointStore.salespoint,
        ...salespointAddressFields,
        address_text: salespointAddressFields.address_text || ''
      });
    }
  }

  const res = await updateAgentPost({
    _data: {
      id: agentId.value,
      ...agentUpdatePayload
    }
  });

  if (res?._code === 0) {
    profileUser.value = {
      ...(profileUser.value || props.user),
      address_of_operation: addressOfOperationPayload
    } as User;

    agentData.value = {
      ...(agentData.value as Agent),
      id: agentId.value || agentData.value?.id || 0,
      user_id: props.user.id,
      ...agentUpdatePayload
    } as Agent;

    const updatedUser = {
      ...props.user,
      phone: phone.value,
      address_of_operation: addressOfOperationPayload
    };

    authModel.user = updatedUser;
    userModel.user = updatedUser;
    isEditMode.value = false;
    addToast({ severity: 'success', summary: 'Профиль', detail: 'Данные сохранены' });

    return;
  }

  addToast({
    severity: 'error',
    summary: 'Профиль',
    detail: res?._message || 'Не удалось сохранить данные'
  });
}

function onBankSelect(bank: DadataBankSuggestion) {
  bik.value = bank.data?.bic || '';
  paymentBank.value = bank.data?.name?.payment || bank.data?.name?.full || '';
  correspondentAccount.value = bank.data?.correspondent_account || '';
  isBankSelectedFromList.value = true;
}

function normalizeDigits(value: unknown, maxLength: number): string {
  if (!value || typeof value === 'object') return '';

  return String(value).replace(/\D/g, '').slice(0, maxLength);
}

function onBikInput(value: unknown) {
  bik.value = normalizeDigits(value, 9);
  paymentBank.value = '';
  correspondentAccount.value = '';
  isBankSelectedFromList.value = false;
}

function onPaymentBankInput(value: string | undefined) {
  paymentBank.value = (value || '').trim();
  isBankSelectedFromList.value = false;
}

function onCorrespondentAccountInput(value: string | undefined) {
  correspondentAccount.value = normalizeDigits(value, 20);
}

function onPaymentAccountInput(value: string | undefined) {
  paymentAccount.value = normalizeDigits(value, 20);
}

function normalizePhone(value: unknown): string {
  const digits = normalizeDigits(value, 11);

  if (digits.length === 11 && (digits.startsWith('7') || digits.startsWith('8'))) {
    return digits.slice(1);
  }

  return digits.slice(0, 10);
}

function onPhoneInput(value: string | null | undefined) {
  phone.value = normalizePhone(value);
}

function preventNonDigits(event: KeyboardEvent) {
  if (
    !/^[0-9]$/.test(event.key) &&
    event.key !== 'Backspace' &&
    event.key !== 'Delete' &&
    event.key !== 'ArrowLeft' &&
    event.key !== 'ArrowRight' &&
    event.key !== 'Tab' &&
    !event.ctrlKey &&
    !event.metaKey
  ) {
    event.preventDefault();
  }
}

onMounted(async () => {
  await salespointStore.setLocalStorageSalespoint();

  const userRes = await getUserPost({ _data: { id: props.user.id } });
  if (userRes?._code === 0 && userRes._data) {
    profileUser.value = userRes._data;
  } else {
    profileUser.value = props.user;
  }

  const listRes = await getAgentListPost({});
  if (listRes?._code !== 0 || !listRes._data) return;

  const selectedSalespointId = salespointStore.salespoint?.id;
  const currentAgent = listRes._data.find(agent => {
    if (agent.user_id !== props.user.id) return false;
    if (!selectedSalespointId) return false;

    return agent.sales_points.includes(selectedSalespointId);
  });

  if (!currentAgent) return;

  agentId.value = currentAgent.id;

  const byIdRes = await getAgentByIdPost({ _data: { id: currentAgent.id } });

  if (byIdRes?._code === 0 && byIdRes._data) {
    agentData.value = byIdRes._data;
  } else {
    agentData.value = currentAgent;
  }

  isBankSelectedFromList.value = Boolean(agentData.value?.payment_bank && agentData.value?.bank_bik);
  resetForm({ values: getFormValues() });
});
</script>
